/**
 * Reads the order-limits app's published configuration.
 *
 * This is the ONLY file in the theme that knows the order-limits app exists.
 * Everything else asks for { min, max, message } and does not care where it
 * came from. If the app is replaced or changes its data shape, only this file
 * changes.
 *
 * It always fails open: any missing, malformed or unmatched configuration
 * yields null, the grid runs ungated, and the app still enforces the minimum
 * at cart and checkout.
 */
(function () {
  // Third-party app config is not guaranteed to keep the shape we expect.
  // A field we expect to be an array can arrive as a string, number, or
  // object instead — guard here rather than letting .map/.some throw.
  function containsId(value, id) {
    if (!Array.isArray(value)) return false;
    return value.map(Number).indexOf(id) !== -1;
  }

  function normalizeTag(tag) {
    return typeof tag === 'string' ? tag.trim().toLowerCase() : '';
  }

  /**
   * Confirmed against a live tag-targeted rule on this shop: the mode is
   * `productTags` and the tag list is on `rule.productTags`. Only that key is
   * read — an alias for a differently-named field would be an unverified guess,
   * and could match some future unrelated `tags` key that means something else.
   */
  function ruleTagList(rule) {
    return Array.isArray(rule.productTags) ? rule.productTags : [];
  }

  function matchesProductTags(rule, productTags) {
    if (!Array.isArray(productTags) || !productTags.length) return false;
    var wanted = ruleTagList(rule).map(normalizeTag).filter(Boolean);
    if (!wanted.length) return false;
    var owned = productTags.map(normalizeTag);
    return wanted.some(function (tag) {
      return owned.indexOf(tag) !== -1;
    });
  }

  /**
   * The app can restrict a rule to customers carrying certain tags, and accepts
   * that field as either an array or a single string. The theme cannot confirm
   * which group the current shopper is in, and gating someone the app would let
   * through is worse than showing no meter, so a customer-scoped rule is
   * skipped and the grid falls back to ungated for everyone.
   */
  function isCustomerScoped(rule) {
    var tags = rule.customerTags;
    if (Array.isArray(tags)) return tags.length > 0;
    if (typeof tags === 'string') return tags.trim().length > 0;
    return false;
  }

  function ruleApplies(rule, productId, collectionMap, productTags) {
    switch (rule.productSelectionType) {
      case 'all':
        return true;
      case 'products':
        return containsId(rule.includedProducts, productId);
      case 'collections':
        if (!Array.isArray(rule.includedCollections)) return false;
        return rule.includedCollections.some(function (handle) {
          return containsId(collectionMap[handle], productId);
        });
      case 'productTags':
        return matchesProductTags(rule, productTags);
      default:
        // Any targeting mode we have not verified against real data is treated
        // as "does not apply" rather than guessed at. Failing open is correct.
        return false;
    }
  }

  function messageFor(rule, min) {
    var messages = rule.messages && rule.messages.en;
    var template = messages && messages.minMessage;
    if (!template) return null;
    return template.replace('{{minimum_quantity}}', String(min));
  }

  /**
   * @param {object|null} settings   parsed contents of the wm-settings tag
   * @param {number|string} productId
   * @param {object} collectionMap   collection handle -> array of product ids
   * @param {Array<string>} [productTags]  this product's tags
   * @returns {{min:number,max:number|null,message:string|null}|null}
   */
  function resolveLimit(settings, productId, collectionMap, productTags) {
    if (!settings || !settings.productLimits) return null;

    var id = Number(productId);
    var map = collectionMap || {};
    var best = null;

    Object.keys(settings.productLimits).forEach(function (key) {
      var rule = settings.productLimits[key];
      if (!rule || rule.status !== 'active' || rule.limitType !== 'quantity') return;
      if (containsId(rule.excludedProducts, id)) return;
      if (isCustomerScoped(rule)) return;
      if (!ruleApplies(rule, id, map, productTags)) return;

      var quantity = rule.limitValue && rule.limitValue.quantity;
      if (!quantity || typeof quantity.min !== 'number' || quantity.min <= 0) return;

      var candidate = {
        min: quantity.min,
        max: typeof quantity.max === 'number' && quantity.max > 0 ? quantity.max : null,
        message: messageFor(rule, quantity.min),
      };

      // Several rules can match; the tightest (highest minimum) is the one
      // checkout will actually enforce.
      if (!best || candidate.min > best.min) best = candidate;
    });

    return best;
  }

  function readJSON(id) {
    try {
      var el = document.getElementById(id);
      if (!el) return null;
      return JSON.parse(el.textContent);
    } catch (error) {
      return null;
    }
  }

  /**
   * @param {number|string} productId
   * @returns {{min:number,max:number|null,message:string|null}|null}
   */
  function getMinimumForProduct(productId) {
    try {
      var settings = readJSON('wm-settings');
      if (!settings) return null;
      var tagData = readJSON('wm-current-product-tags');
      var productTags = tagData && Array.isArray(tagData.tags) ? tagData.tags : [];
      return resolveLimit(settings, productId, readJSON('wm-collection-map') || {}, productTags);
    } catch (error) {
      return null;
    }
  }

  window.BulkOrderLimits = {
    resolveLimit: resolveLimit,
    getMinimumForProduct: getMinimumForProduct,
  };
})();
